import React, { useEffect } from "react";

// Replace by lowercase and remove diacritics
const cleanString = (s: string) =>
  s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

type InputSelector = {
  attribute: "name" | "id";
  value: string;
};

// Require type="text" because selectionStart/selectionEnd/setRangeText are not supported otherwise
export const useCleanLoginInput = (
  inputSelectors: InputSelector | Array<InputSelector>,
  container?: React.RefObject<Node | undefined>
) => {
  const inputSelectorsArray = Array.isArray(inputSelectors) ? inputSelectors : [inputSelectors];

  useEffect(() => {
    const isTargetedInput = (e: EventTarget | null): e is HTMLInputElement =>
      e instanceof HTMLInputElement &&
      (!container || container.current?.contains(e) === true) &&
      inputSelectorsArray.some(
        ({ attribute, value }) => (e as HTMLInputElement)[attribute] === value
      );
    const onBeforeInputListener = (e: InputEvent) => {
      if (isTargetedInput(e.target)) {
        if (!e.data) {
          return;
        }

        const cleanData = e.data && cleanString(e.data);

        // if data is already clean, keep regular flow
        if (cleanData === e.data) {
          return;
        }

        e.preventDefault();

        const input = e.target;

        // composition edition is handled using compositionend event
        if (e.inputType === "insertCompositionText") {
          return;
        }

        // replace input data by cleandata
        input.setRangeText(cleanData, input.selectionStart!, input.selectionEnd!, "end");
      }
    };

    const onCompositionEndListener = (e: Event) => {
      if (isTargetedInput(e.target)) {
        e.preventDefault();

        const input = e.target;
        const cleanValue = cleanString(input.value);
        const { selectionStart, selectionEnd } = input;

        input.value = cleanValue;
        input.setSelectionRange(selectionStart, selectionEnd);
      }
    };

    window.addEventListener("beforeinput", onBeforeInputListener);
    window.addEventListener("compositionend", onCompositionEndListener);

    return () => {
      window.removeEventListener("beforeinput", onBeforeInputListener);
      window.removeEventListener("compositionend", onCompositionEndListener);
    };
  }, [
    inputSelectorsArray
      .map(({ attribute, value }) => `${attribute}=${value}`)
      .sort()
      .join("/"),
  ]);
};
